import React, { useState, useEffect } from "react";
import { useFormikContext } from "formik";
import { Button, CheckBox } from "components/atoms";
import { DocumentUploadModal } from "components/molecules";
import { useUserData, useUpload, useFileValidation } from "hooks";
import { documentService } from "services";
import { GenuineStudentFormValues, FileWithType } from "types";

const ALLOWED_KEYS: Record<string, Set<string>> = {
  extractedData: new Set(["genuineStudentData"]),
};

const GSSOPSection: React.FC = () => {
  const { values, setFieldValue } =
    useFormikContext<GenuineStudentFormValues>();
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [uploadedDocuments, setUploadedDocuments] = useState<{
    statementOfPurpose?: { id: string; fileName: string };
  }>({});
  const userDataHook = useUserData();
  const { userData } = userDataHook;

  const { validateFileSync, getAcceptedFileTypes, getMaxFileSize } =
    useFileValidation({
      maxFileSize: 5 * 1024 * 1024, // 5MB
      enableMagicNumberValidation: false, // Disabled for sync validation
      sanitizeMetadata: true,
    });

  const {
    stage,
    uploadProgress,
    successMessage,
    errorMessage,
    handleFileUpload,
    handleFileRemove,
    handleError,
  } = useUpload(userDataHook, ALLOWED_KEYS, {
    enableExtraction: false,
    customUploadMessage: "Statement of Purpose uploaded successfully.",
  });

  const statementOfPurposeTypes = ["STATEMENT_OF_PURPOSE"];

  useEffect(() => {
    if (userData?.id) {
      documentService
        .fetchDocumentsWithMapping(statementOfPurposeTypes)
        .then(setUploadedDocuments);
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData?.id]);

  const getDocumentFields = () => {
    return [
      {
        id: "statementofpurpose",
        label: "Statement of Purpose",
        description: "Your personal statement (1,500-2,000 words recommended)",
        accept: ".pdf,.doc,.docx",
        required: true,
        uploaded: !!uploadedDocuments.statementOfPurpose,
      },
    ];
  };

  const handleUploadWithRefresh = async (files: FileWithType[]) => {
    try {
      await handleFileUpload(files);
      setTimeout(async () => {
        if (userData?.id) {
          const docs = await documentService.fetchDocumentsWithMapping(
            statementOfPurposeTypes,
          );

          setUploadedDocuments(docs);
        }
      }, 1000);
      return { files, results: [] };
    } catch (error) {
      console.error("Upload failed:", error);
      return { files: [], results: [] };
    }
  };

  return (
    <div className="sop-section">
      <div className="form-section">
        <div className="section-header">
          <h4>Statement of Purpose</h4>
          <p>
            Your Statement of Purpose is a critical component that provides the
            narrative depth to complement the factual data in this assessment.
          </p>
        </div>

        <div className="sop-guidelines-box">
          <h5>Important Guidelines</h5>
          <div className="guidelines-content">
            <p>
              <strong>What to Include in Your Statement of Purpose:</strong>
            </p>
            <ul>
              <li>Your unique personal story and motivations</li>
              <li>
                Detailed analysis of why this specific course and institution
              </li>
              <li>Concrete career plans with specific roles and timeline</li>
              <li>Deep explanation of course selection reasoning</li>
              <li>Evidence of serious preparation and research</li>
              <li>How you'll contribute to your home country upon return</li>
            </ul>

            <p>
              <strong>What NOT to Include:</strong>
            </p>
            <ul>
              <li>❌ Generic statements without specific examples</li>
              <li>❌ Content copied from institution websites</li>
              <li>❌ Unsupported claims without evidence</li>
            </ul>

            <p className="word-count-note">
              <strong>Recommended length:</strong> 1,500-2,000 words
            </p>
          </div>

          <div className="guidelines-checkboxes">
            <CheckBox
              checked={values.sopGuidelinesAcknowledged || false}
              onChange={(checked) =>
                setFieldValue("sopGuidelinesAcknowledged", checked)
              }
              label="I have read and understood the SOP guidelines"
            />
            <CheckBox
              checked={values.sopRequirementsUnderstood || false}
              onChange={(checked) =>
                setFieldValue("sopRequirementsUnderstood", checked)
              }
              label="I understand that my SOP must provide narrative depth."
            />
          </div>
        </div>

        <div className="documents-section">
          <div className="upload-header">
            <Button
              type="button"
              text="Upload Statement of Purpose"
              onClick={() => setShowDocumentModal(true)}
              variant="border"
              width="12rem"
            />
            <div className="docs-counter">
              <span className="uploaded-count">
                {documentService.countUploadedDocs(getDocumentFields())}
              </span>
              <span className="separator">/</span>
              <span className="total-count">
                {documentService.countRequiredDocs(getDocumentFields())}
              </span>
              <span className="docs-label">documents</span>
            </div>
          </div>

          {(!values.sopGuidelinesAcknowledged ||
            !values.sopRequirementsUnderstood) && (
            <p className="upload-note">
              Please acknowledge the guidelines above before uploading
            </p>
          )}
          <div className="sop-tips">
            <h5>Writing Tips</h5>
            <ul>
              <li>
                <strong>Show, don't tell:</strong> Use specific examples and
                stories
              </li>
              <li>
                <strong>Be specific:</strong> Name exact modules, faculty,
                resources
              </li>
              <li>
                <strong>Connect the dots:</strong> Explain WHY experiences
                matter
              </li>
              <li>
                <strong>Stay authentic:</strong> Write in your genuine voice
              </li>
            </ul>
          </div>
        </div>
      </div>

      <DocumentUploadModal
        isOpen={showDocumentModal}
        onClose={() => {
          handleError("");
          setShowDocumentModal(false);
        }}
        title="Upload Statement of Purpose"
        description="Please upload your SOP document (PDF, DOC, or DOCX format):"
        documents={getDocumentFields()}
        onFileUpload={handleUploadWithRefresh}
        onFileRemove={handleFileRemove}
        validateFile={validateFileSync}
        acceptedFileTypes={getAcceptedFileTypes()}
        onError={handleError}
        errorMessage={errorMessage}
        successMessage={successMessage}
        maxFileSize={getMaxFileSize()}
        stage={stage}
        uploadProgress={uploadProgress}
      />
    </div>
  );
};

export default GSSOPSection;
